// Multi-profile selector component for manager team view
// CREATED: Dropdown with checkboxes for selecting multiple operators
// UPDATED: Refactored to use custom implementation instead of BaseFilterDropdown
// UPDATED: Integrated search bar for filtering members by name
// UPDATED: Removed RoleBadge display for cleaner interface
// UPDATED: Added "Clear All" button in header (visible when items selected)
// UPDATED: Removed redundant footer showing "X membres sélectionnés"
// UPDATED: Unified mobile/desktop UX - multi-selection enabled on all devices
// UPDATED: Auto/manual selection mode toggle now integrated INSIDE the button with vertical separator
// singleSelection prop available for specific use cases but not used by default

import { useState, useRef, useEffect } from 'react';
import { Users, Wand2, Hand, ChevronDown, Search } from 'lucide-react';
import { getFullName } from '../../lib/supabase';
import { useResponsiveLayout } from '../../hooks/useResponsiveLayout';
import { BottomSheet } from '../ui/BottomSheet';

interface Profile {
  id: string;
  first_name: string;
  last_name: string;
  username: string;
  role: 'operator' | 'manager' | 'director';
}

interface MultiProfileSelectorProps {
  profiles: Profile[];
  selectedProfileIds: string[];
  onSelectionChange: (profileIds: string[]) => void;
  label?: string;
  compact?: boolean;
  singleSelection?: boolean;
  selectionMode?: 'auto' | 'manual';
  onSelectionModeChange?: (mode: 'auto' | 'manual') => void;
}

export function MultiProfileSelector({
  profiles,
  selectedProfileIds,
  onSelectionChange,
  label = 'Équipe',
  compact = false,
  singleSelection = false,
  selectionMode,
  onSelectionModeChange
}: MultiProfileSelectorProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const { isMobile } = useResponsiveLayout();

  const showToggle = compact && selectionMode && onSelectionModeChange;
  const isAuto = selectionMode === 'auto';
  const hasSelection = selectedProfileIds.length > 0;

  // Close on click outside (only for desktop dropdown)
  useEffect(() => {
    const isUsingBottomSheet = compact && isMobile;

    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setSearchQuery('');
      }
    };

    if (isOpen && !isUsingBottomSheet) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, compact, isMobile]);

  // Focus search input when dropdown opens
  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      setTimeout(() => searchInputRef.current?.focus(), 100);
    }
  }, [isOpen]);

  // Filter profiles based on search query
  const filteredProfiles = searchQuery
    ? profiles.filter(profile => {
        const fullName = getFullName(profile).toLowerCase();
        return fullName.includes(searchQuery.toLowerCase());
      })
    : profiles;

  const handleToggleProfile = (profileId: string) => {
    if (singleSelection) {
      onSelectionChange([profileId]);
      setIsOpen(false);
      setSearchQuery('');
    } else {
      const newSelection = selectedProfileIds.includes(profileId)
        ? selectedProfileIds.filter(id => id !== profileId)
        : [...selectedProfileIds, profileId];
      onSelectionChange(newSelection);
    }
  };

  const handleSelectAll = () => {
    if (selectedProfileIds.length === profiles.length) {
      onSelectionChange([]);
    } else {
      onSelectionChange(profiles.map(p => p.id));
    }
  };

  const handleClearAll = () => {
    onSelectionChange([]);
    setSearchQuery('');
  };

  // Handle toggle mode click - prevent dropdown from opening
  const handleToggleMode = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onSelectionModeChange) {
      onSelectionModeChange(isAuto ? 'manual' : 'auto');
    }
  };

  const getCompactLabel = (): string => {
    if (selectedProfileIds.length === 0) {
      return label;
    }
    if (selectedProfileIds.length === 1) {
      const profile = profiles.find(p => p.id === selectedProfileIds[0]);
      return profile ? getFullName(profile) : label;
    }
    return label;
  };

  // Render dropdown content (shared between BottomSheet and desktop dropdown)
  const renderContent = () => (
    <>
      {/* Search bar */}
      <div className="px-4 py-3 border-b border-gray-100">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
          <input
            ref={searchInputRef}
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Rechercher un utilisateur..."
            className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          />
        </div>
      </div>

      {/* Select All Option (only in multi-selection mode) */}
      {!singleSelection && (
        <div className="px-6 py-3.5 border-b border-gray-100">
          <label className="flex items-center gap-3 hover:bg-gray-50 cursor-pointer transition-colors py-1 min-h-[48px]">
            <input
              type="checkbox"
              checked={selectedProfileIds.length === profiles.length}
              onChange={handleSelectAll}
              className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
            />
            <span className="text-sm text-gray-700 font-medium">
              Sélectionner tout
            </span>
          </label>
        </div>
      )}

      {/* Profiles list */}
      {filteredProfiles.length === 0 ? (
        <div className="px-6 py-3.5 text-center text-gray-500 text-sm">
          {searchQuery ? 'Aucun résultat trouvé' : 'Aucun utilisateur disponible'}
        </div>
      ) : (
        filteredProfiles.map(profile => {
          const isSelected = selectedProfileIds.includes(profile.id);

          return (
            <label
              key={profile.id}
              className="flex items-center gap-3 px-6 py-3.5 min-h-[48px] hover:bg-gray-50 cursor-pointer transition-colors"
            >
              <input
                type={singleSelection ? "radio" : "checkbox"}
                checked={isSelected}
                onChange={() => handleToggleProfile(profile.id)}
                className={`w-4 h-4 text-primary-600 border-gray-300 focus:ring-primary-500 ${
                  singleSelection ? 'rounded-full' : 'rounded'
                }`}
              />
              <span className="text-sm text-gray-700 truncate flex-1">
                {getFullName(profile)}
              </span>
            </label>
          );
        })
      )}
    </>
  );

  // Compact mode with integrated toggle inside button
  if (compact) {
    return (
      <div ref={dropdownRef} className="relative">
        {/* Custom button with integrated toggle and vertical separator */}
        <button
          onClick={() => setIsOpen(!isOpen)}
          className={`flex items-center border rounded-lg transition-all text-sm font-medium overflow-hidden ${
            hasSelection
              ? 'bg-primary-50 border-primary-300 text-primary-700 hover:bg-primary-100'
              : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
          }`}
        >
          {/* Toggle section (left side) - clickable to change mode */}
          {showToggle && (
            <>
              <div
                onClick={handleToggleMode}
                className={`flex flex-col items-center justify-center gap-0.5 w-14 py-2.5 transition-colors ${
                  isAuto
                    ? 'hover:bg-primary-100'
                    : 'hover:bg-gray-100'
                }`}
                title={isAuto ? 'Mode automatique : opérateurs sélectionnés selon les services' : 'Mode manuel : sélection explicite des opérateurs'}
              >
                {isAuto ? <Wand2 size={12} /> : <Hand size={12} />}
                <span className="text-[10px] font-medium whitespace-nowrap leading-tight">
                  {isAuto ? 'Auto' : 'Manuel'}
                </span>
              </div>

              {/* Vertical separator - visual boundary between toggle and selector */}
              <div className={`w-px self-stretch ${
                hasSelection ? 'bg-primary-300' : 'bg-gray-300'
              }`} />
            </>
          )}

          {/* Selector content (right side) - clickable to open dropdown */}
          <div className="flex items-center gap-2 px-4 py-2.5">
            <Users size={16} />
            <span>{getCompactLabel()}</span>
            {selectedProfileIds.length > 1 && (
              <span className="inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-primary-600 text-white text-xs font-semibold">
                {selectedProfileIds.length}
              </span>
            )}
            <ChevronDown size={16} className={`transition-transform ${isOpen ? 'rotate-180' : ''}`} />
          </div>
        </button>

        {/* Use BottomSheet on mobile, Dropdown on desktop */}
        {isMobile ? (
          <BottomSheet
            isOpen={isOpen}
            onClose={() => {
              setIsOpen(false);
              setSearchQuery('');
            }}
            title={singleSelection ? 'Sélectionner un utilisateur' : 'Sélectionner des utilisateurs'}
          >
            {/* Clear All button in BottomSheet header */}
            {selectedProfileIds.length > 0 && (
              <div className="flex items-center justify-end px-4 py-2 border-b border-gray-100 bg-gray-50">
                <button
                  onClick={handleClearAll}
                  className="text-xs text-primary-600 hover:text-primary-700 font-medium"
                >
                  Tout effacer
                </button>
              </div>
            )}
            {renderContent()}
          </BottomSheet>
        ) : (
          isOpen && (
            <div className="absolute z-50 mt-2 left-0 min-w-[280px] bg-white border border-gray-200 rounded-lg shadow-lg overflow-y-auto">
              <div className="max-h-96 py-2">
                {/* Clear All button in dropdown header */}
                {selectedProfileIds.length > 0 && (
                  <div className="flex items-center justify-end px-4 py-2 border-b border-gray-100">
                    <button
                      onClick={handleClearAll}
                      className="text-xs text-primary-600 hover:text-primary-700 font-medium"
                    >
                      Tout effacer
                    </button>
                  </div>
                )}
                {renderContent()}
              </div>
            </div>
          )
        )}
      </div>
    );
  }

  // Non-compact mode - not supported with toggle integration
  return (
    <div className="text-sm text-gray-500">
      Mode non-compact non supporté avec le toggle. Utilisez compact={true}.
    </div>
  );
}
